/* cds-slider.js - PREMIUM VERSION v5 */
class CdsSlider extends HTMLElement {
  constructor() {
    super();
    this._drag = {
      active: false,
      startX: 0,
      startScrollLeft: 0,
      moved: false,
      touchLocked: null,
      touchStartX: 0,
      touchStartY: 0,
      samples: [], // Ring buffer for velocity
    };
    this._raf = null;
  }

  connectedCallback() {
    this.track = this.querySelector('.cds-slider-track');
    this.items = Array.from(this.querySelectorAll('.cds-slider-item'));
    this.prevBtn = this.querySelector('.cds-slider-prev');
    this.nextBtn = this.querySelector('.cds-slider-next');
    this.dotsContainer = this.querySelector('.cds-slider-dots');
    this.progressFill = this.querySelector('.cds-progress-fill');
    this.counterEl = this.querySelector('.cds-slider-counter');

    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed) || 3000;
    this.autoplayInterval = null;
    this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.init();
  }

  init() {
    if (!this.track || this.items.length === 0) return;

    this.checkIfSliderEnabled();
    window.addEventListener('resize', () => this.checkIfSliderEnabled());

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollByDirection(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollByDirection(1));

    this.setupDots();
    this.setupObserver();
    this.setupDragAndTouch();
    this.setupKeyboard();
    this.setupResizeObserver();

    if (this.autoplayEnabled) {
      this.startAutoplay();
      this.track.addEventListener('mouseenter', () => this.stopAutoplay());
      this.track.addEventListener('mouseleave', () => this.startAutoplay());
    }
  }

  // ─── ANIMATION ENGINE ─────────────────────────────────────────────────────
  // rAF physics used ONLY after drag release (momentum + snap).
  // Arrows, dots, autoplay use native scrollTo — so scroll events fire normally.

  _cancelAnimation() {
    if (this._raf) {
      cancelAnimationFrame(this._raf);
      this._raf = null;
    }
  }

  // Called after a drag flick — exponential friction decay, then spring snap
  _runMomentum(velocityPxFrame) {
    if (this._reducedMotion) {
      this._snapToNearest(true);
      return;
    }

    const FRICTION = 0.92;        // velocity kept per frame — higher = more glide
    const STOP_VELOCITY = 0.5;    // px/frame — when to hand off to snap

    const tick = () => {
      velocityPxFrame *= FRICTION;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const next = Math.max(0, Math.min(maxScroll, this.track.scrollLeft - velocityPxFrame));
      this.track.scrollLeft = next;

      const hitEdge = next <= 0 || next >= maxScroll;
      if (Math.abs(velocityPxFrame) < STOP_VELOCITY || hitEdge) {
        this._snapToNearest(false);
        return;
      }
      this._raf = requestAnimationFrame(tick);
    };
    this._raf = requestAnimationFrame(tick);
  }

  // Spring ease to the nearest item snap point
  _snapToNearest(instant) {
    this._cancelAnimation();
    const track = this.track;
    const center = track.scrollLeft + track.clientWidth / 2;

    let closest = this.items[0];
    let minDist = Infinity;
    this.items.forEach(item => {
      const d = Math.abs((item.offsetLeft + item.offsetWidth / 2) - center);
      if (d < minDist) { minDist = d; closest = item; }
    });

    const target = Math.max(0,
      Math.min(closest.offsetLeft - track.offsetLeft, track.scrollWidth - track.clientWidth)
    );

    if (instant || this._reducedMotion) {
      track.scrollLeft = target;
      track.style.scrollSnapType = '';
      return;
    }

    const SPRING = 0.12;
    const STOP = 0.5;
    const tick = () => {
      const rem = target - track.scrollLeft;
      if (Math.abs(rem) < STOP) {
        track.scrollLeft = target;
        track.style.scrollSnapType = '';
        return;
      }
      track.scrollLeft += rem * SPRING;
      this._raf = requestAnimationFrame(tick);
    };
    this._raf = requestAnimationFrame(tick);
  }

  // ─── VELOCITY SAMPLING ────────────────────────────────────────────────────

  _recordSample(x) {
    this._drag.samples.push({ x, t: performance.now() });
    if (this._drag.samples.length > 5) this._drag.samples.shift();
  }

  _getVelocityPxFrame() {
    const s = this._drag.samples;
    if (s.length < 2) return 0;
    const dt = s[s.length - 1].t - s[0].t;
    if (dt < 1) return 0;
    const velocityPxMs = (s[s.length - 1].x - s[0].x) / dt;
    return velocityPxMs * 16.67; // convert to px/frame at 60fps
  }

  // ─── DRAG & TOUCH ─────────────────────────────────────────────────────────

  setupDragAndTouch() {
    const track = this.track;

    // ── MOUSE ──
    track.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      this._cancelAnimation();
      this._beginDrag(e.clientX);
      track.style.cursor = 'grabbing';

      const onMove = (e) => this._continueDrag(e.clientX);
      const onUp = () => {
        this._releaseDrag();
        track.style.cursor = '';
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    });

    // ── TOUCH ──
    track.addEventListener('touchstart', (e) => {
      const t = e.touches[0];
      this._cancelAnimation();
      this._drag.touchStartX = t.clientX;
      this._drag.touchStartY = t.clientY;
      this._drag.touchLocked = null;
      this._drag.active = false;
      this._drag.moved = false;
      this._drag.samples = [];
      this._drag.startScrollLeft = track.scrollLeft;
      this._drag.startX = t.clientX;
      this._recordSample(t.clientX);
    }, { passive: true });

    track.addEventListener('touchmove', (e) => {
      const t = e.touches[0];
      const dx = Math.abs(t.clientX - this._drag.touchStartX);
      const dy = Math.abs(t.clientY - this._drag.touchStartY);

      if (this._drag.touchLocked === null) {
        if (dx < 8 && dy < 8) return;
        this._drag.touchLocked = dx > dy ? 'h' : 'v';
      }

      if (this._drag.touchLocked === 'v') {
        this._drag.active = false;
        return;
      }

      if (!this._drag.active) {
        this._drag.active = true;
        track.style.scrollSnapType = 'none';
        this.stopAutoplay();
      }

      e.preventDefault();
      this._recordSample(t.clientX);
      this._continueDrag(t.clientX);
    }, { passive: false });

    track.addEventListener('touchend', () => {
      if (this._drag.touchLocked === 'h') {
        this._releaseDrag();
      } else {
        this._drag.active = false;
        this._drag.moved = false;
      }
      this._drag.touchLocked = null;
    });

    track.addEventListener('touchcancel', () => {
      this._drag.active = false;
      this._drag.moved = false;
      this._drag.touchLocked = null;
      this._drag.samples = [];
      track.style.scrollSnapType = '';
      if (this.autoplayEnabled) this.startAutoplay();
    });

    track.addEventListener('click', (e) => {
      if (this._drag.moved) {
        e.preventDefault();
        e.stopPropagation();
        this._drag.moved = false;
      }
    }, true);
  }

  _beginDrag(clientX) {
    this._drag.active = true;
    this._drag.moved = false;
    this._drag.startX = clientX;
    this._drag.startScrollLeft = this.track.scrollLeft;
    this._drag.samples = [];
    this._recordSample(clientX);
    this.track.style.scrollSnapType = 'none';
    this.stopAutoplay();
  }

  _continueDrag(clientX) {
    if (!this._drag.active) return;
    const dx = clientX - this._drag.startX;
    if (!this._drag.moved && Math.abs(dx) < 3) return;
    this._drag.moved = true;
    this._recordSample(clientX);

    const track = this.track;
    const maxScroll = track.scrollWidth - track.clientWidth;
    let next = this._drag.startScrollLeft - dx;

    // Rubber-band at edges
    if (next < 0) {
      next = -Math.pow(Math.abs(next), 0.7) * 0.6;
    } else if (next > maxScroll) {
      next = maxScroll + Math.pow(next - maxScroll, 0.7) * 0.6;
    }

    track.scrollLeft = next;
  }

  _releaseDrag() {
    if (!this._drag.active) return;
    this._drag.active = false;

    if (!this._drag.moved) {
      this.track.style.scrollSnapType = '';
      if (this.autoplayEnabled) this.startAutoplay();
      return;
    }

    const vel = this._getVelocityPxFrame();
    if (Math.abs(vel) > 2) {
      this._runMomentum(vel);
    } else {
      this._snapToNearest(false);
    }

    if (this.autoplayEnabled) this.startAutoplay();
  }

  // ─── KEYBOARD ─────────────────────────────────────────────────────────────

  setupKeyboard() {
    if (!this.track.hasAttribute('tabindex')) this.track.setAttribute('tabindex', '0');
    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.scrollByDirection(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); this.scrollByDirection(1); }
    });
  }

  // ─── ARROWS & DOTS — use native scrollTo so scroll events fire correctly ──

  scrollByDirection(direction) {
    // Cancel any rAF animation so it doesn't fight native scroll
    this._cancelAnimation();
    const itemWidth = this.items[0].getBoundingClientRect().width;
    const gap = parseInt(getComputedStyle(this.track).gap) || 0;
    this.track.scrollBy({
      left: (itemWidth + gap) * direction,
      behavior: this._reducedMotion ? 'auto' : 'smooth',
    });
  }

  scrollToIndex(index) {
    this._cancelAnimation();
    const target = this.items[index];
    if (!target) return;
    this.track.scrollTo({
      left: target.offsetLeft - this.track.offsetLeft,
      behavior: this._reducedMotion ? 'auto' : 'smooth',
    });
  }

  // ─── DOTS ────────────────────────────────────────────────────────────────

  setupDots() {
    if (!this.dotsContainer) return;
    this.items.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.classList.add('cds-dot');
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.addEventListener('click', () => this.scrollToIndex(i));
      this.dotsContainer.appendChild(dot);
    });
  }

  // ─── SCROLL OBSERVER — arrows + indicators ────────────────────────────────

  setupObserver() {
    // IntersectionObserver for indicator updates
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.updateIndicators(this.items.indexOf(entry.target));
        }
      });
    }, { root: this.track, threshold: 0.5 });
    this.items.forEach(item => io.observe(item));

    // Arrow state on every scroll event (native scroll fires reliably)
    const updateArrows = () => {
      if (!this.prevBtn || !this.nextBtn) return;
      this.prevBtn.disabled = this.track.scrollLeft <= 5;
      this.nextBtn.disabled =
        Math.ceil(this.track.scrollLeft + this.track.clientWidth) >= this.track.scrollWidth - 15;
    };

    this.track.addEventListener('scroll', updateArrows, { passive: true });
    // Run once after layout settles
    requestAnimationFrame(() => requestAnimationFrame(updateArrows));
    window.addEventListener('resize', updateArrows);
  }

  updateIndicators(index) {
    if (this.dotsContainer) {
      this.dotsContainer.querySelectorAll('.cds-dot').forEach((d, i) => {
        d.classList.toggle('is-active', i === index);
      });
      const active = this.dotsContainer.querySelectorAll('.cds-dot')[index];
      if (active) {
        this.dotsContainer.scrollTo({
          left: active.offsetLeft - this.dotsContainer.clientWidth / 2 + active.clientWidth / 2,
          behavior: 'smooth',
        });
      }
    }
    if (this.counterEl) this.counterEl.textContent = `${index + 1} / ${this.items.length}`;
    if (this.progressFill) {
      this.progressFill.style.width = `${((index + 1) / this.items.length) * 100}%`;
    }
  }

  // ─── AUTOPLAY — native scrollTo, no rAF conflict ─────────────────────────

  startAutoplay() {
    if (this.autoplayInterval) return;
    this.autoplayInterval = setInterval(() => {
      this._cancelAnimation(); // clear any lingering rAF before autoplay scroll
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 10;
      this.track.scrollTo({
        left: atEnd ? 0 : this.track.scrollLeft + this.items[0].getBoundingClientRect().width +
              (parseInt(getComputedStyle(this.track).gap) || 0),
        behavior: this._reducedMotion ? 'auto' : 'smooth',
      });
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    clearInterval(this.autoplayInterval);
    this.autoplayInterval = null;
  }

  // ─── SLIDER ENABLED CHECK ─────────────────────────────────────────────────

  checkIfSliderEnabled() {
    const isDesktop = window.innerWidth >= 768;
    const isEnabled = (isDesktop ? this.dataset.desktopEnabled : this.dataset.mobileEnabled) === 'true';

    if (!isEnabled) {
      this.track.style.overflowX = 'visible';
      if (this.prevBtn) this.prevBtn.style.display = 'none';
      if (this.nextBtn) this.nextBtn.style.display = 'none';
      const controls = this.querySelector('.cds-slider-controls');
      if (controls) controls.style.display = 'none';
      this.stopAutoplay();
      return;
    }

    const controls = this.querySelector('.cds-slider-controls');
    if (controls) controls.style.display = 'flex';
    this.track.style.overflowX = 'auto';

    const visibleItems = isDesktop
      ? parseInt(getComputedStyle(this).getPropertyValue('--desktop-items'))
      : parseInt(getComputedStyle(this).getPropertyValue('--mobile-items'));

    const needsSlider = this.items.length > visibleItems;
    if (this.prevBtn) this.prevBtn.style.display = needsSlider ? '' : 'none';
    if (this.nextBtn) this.nextBtn.style.display = needsSlider ? '' : 'none';
    if (this.dotsContainer) this.dotsContainer.style.display = needsSlider ? 'flex' : 'none';
    if (this.progressFill) this.progressFill.parentElement.style.display = needsSlider ? '' : 'none';
    if (this.counterEl) this.counterEl.style.display = needsSlider ? '' : 'none';

    if (needsSlider && this.autoplayEnabled) {
      this.startAutoplay();
    } else if (!needsSlider) {
      this.stopAutoplay();
    }
  }

  setupResizeObserver() {
    new ResizeObserver(entries => {
      for (const e of entries) {
        if (e.contentRect.width > 0) this.checkIfSliderEnabled();
      }
    }).observe(this);
  }
}

customElements.define('cds-slider', CdsSlider);
